/**
 * Regra de recebimento da comissão do PARCEIRO (parametrização aprovada em 24/10/2026) — preenchida uma única vez no
 * cadastro do parceiro e COPIADA para a versão da Proposta no envio. Regras puras, compartilhadas entre o servidor e as
 * telas (sem dependências de Node). O Contas a Receber usa a cópia congelada na operação; alterar o cadastro depois não
 * muda acordos anteriores. O que não estiver definido fica "A confirmar": nada é calculado por suposição.
 *
 * Prazos distintos (nunca somados duas vezes):
 *  - reconhecimento do pagamento: dias entre o pagamento do Cliente ao parceiro e o parceiro reconhecer esse pagamento
 *    (ex.: boleto 30 dias + 2 dias de reconhecimento = comissão prevista para NF + 32 dias);
 *  - repasse: dias contados a partir do MARCO acordado (pagamento do Cliente, reconhecimento do pagamento, liberação,
 *    solicitação de resgate, nota da Inttrix ou outro).
 */

export const A_CONFIRMAR = 'A confirmar';

export type TipoLiberacao = 'proporcional' | 'integral_primeira' | 'mensalidade' | 'marco_prazo' | 'dias_apos_pagamento';
export type MarcoLiberacao = 'implantacao' | 'remessa';
export type MarcoRepasse = 'pagamento_cliente' | 'reconhecimento' | 'liberacao' | 'solicitacao_resgate' | 'nf_inttrix' | 'outro';
export type TipoDias = 'corridos' | 'uteis';
export type OrigemConfirmacao = 'sistema_distribuidor' | 'informacao_solicitada' | 'demonstrativo';
export type Modalidade = 'venda' | 'locacao_mensal' | 'locacao_projeto' | 'trade_in';

export const TIPOS_LIBERACAO: { id: TipoLiberacao; rotulo: string }[] = [
  { id: 'proporcional', rotulo: 'Proporcional a cada parcela paga pelo Cliente' },
  { id: 'integral_primeira', rotulo: 'Integral após a 1ª parcela paga' },
  { id: 'mensalidade', rotulo: 'Por mensalidade paga pelo Cliente' },
  { id: 'marco_prazo', rotulo: 'Marco + prazo' },
  { id: 'dias_apos_pagamento', rotulo: 'Dias após o pagamento do comprador ao Cliente' },
];
export const MARCOS_LIBERACAO: { id: MarcoLiberacao; rotulo: string }[] = [
  { id: 'implantacao', rotulo: 'Implantação concluída' },
  { id: 'remessa', rotulo: 'Remessa (1ª NF)' },
];
export const MARCOS_REPASSE: { id: MarcoRepasse; rotulo: string }[] = [
  { id: 'pagamento_cliente', rotulo: 'Pagamento do Cliente' },
  { id: 'reconhecimento', rotulo: 'Reconhecimento do pagamento pelo parceiro' },
  { id: 'liberacao', rotulo: 'Liberação do item' },
  { id: 'solicitacao_resgate', rotulo: 'Solicitação do resgate' },
  { id: 'nf_inttrix', rotulo: 'Emissão da NF da Inttrix' },
  { id: 'outro', rotulo: 'Outro (descrever)' },
];
export const ORIGENS_CONFIRMACAO: { id: OrigemConfirmacao; rotulo: string }[] = [
  { id: 'sistema_distribuidor', rotulo: 'Sistema do distribuidor' },
  { id: 'informacao_solicitada', rotulo: 'Informação solicitada ao parceiro' },
  { id: 'demonstrativo', rotulo: 'Demonstrativo do parceiro' },
];
export const rotuloDe = <T extends string>(lista: { id: T; rotulo: string }[], id: T | null | undefined) => (id ? (lista.find((x) => x.id === id)?.rotulo ?? A_CONFIRMAR) : A_CONFIRMAR);

/** Liberações que dependem do pagamento do Cliente ao parceiro (onde o prazo de reconhecimento se aplica). */
export const LIBERACOES_POR_PAGAMENTO: TipoLiberacao[] = ['proporcional', 'integral_primeira', 'mensalidade'];

/** Regra de recebimento do parceiro (campos nulos = "A confirmar"). Guardada em parceiro.regra_recebimento. */
export interface RegraRecebimento {
  liberacao: TipoLiberacao | null;
  marcoLiberacao: MarcoLiberacao | null;
  diasLiberacao: number | null;
  tipoDiasLiberacao: TipoDias | null;
  /** dias entre o pagamento do Cliente ao parceiro e o reconhecimento desse pagamento pelo parceiro */
  diasReconhecimento: number | null;
  tipoDiasReconhecimento: TipoDias | null;
  marcoRepasse: MarcoRepasse | null;
  /** descrição do marco quando "Outro" (sem data projetável) */
  marcoRepasseDescricao: string;
  diasRepasse: number | null;
  tipoDiasRepasse: TipoDias | null;
  confirmacao: OrigemConfirmacao | null;
  observacoes: string;
}
export const regraRecebimentoVazia = (): RegraRecebimento => ({
  liberacao: null,
  marcoLiberacao: null,
  diasLiberacao: null,
  tipoDiasLiberacao: null,
  diasReconhecimento: null,
  tipoDiasReconhecimento: null,
  marcoRepasse: null,
  marcoRepasseDescricao: '',
  diasRepasse: null,
  tipoDiasRepasse: null,
  confirmacao: null,
  observacoes: '',
});

const ids = <T extends string>(lista: { id: T }[]) => lista.map((x) => x.id);
const umDe = <T extends string>(x: unknown, validos: T[]): T | null => (typeof x === 'string' && (validos as string[]).includes(x) ? (x as T) : null);
const dias = (x: unknown): number | null => (typeof x === 'number' && Number.isInteger(x) && x >= 0 && x <= 730 ? x : null);
const tipoDias = (x: unknown): TipoDias | null => umDe(x, ['corridos', 'uteis'] as TipoDias[]);
const texto = (x: unknown, max: number) => (typeof x === 'string' ? x.trim().slice(0, max) : '');

/** Normaliza o que vier da tela ou do banco: fora do formato vira "A confirmar". */
export function normalizarRegraRecebimento(v: unknown): RegraRecebimento {
  const o = v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : {};
  const marcoRepasse = umDe(o.marcoRepasse, ids(MARCOS_REPASSE));
  return {
    liberacao: umDe(o.liberacao, ids(TIPOS_LIBERACAO)),
    marcoLiberacao: umDe(o.marcoLiberacao, ids(MARCOS_LIBERACAO)),
    diasLiberacao: dias(o.diasLiberacao),
    tipoDiasLiberacao: tipoDias(o.tipoDiasLiberacao),
    diasReconhecimento: dias(o.diasReconhecimento),
    tipoDiasReconhecimento: tipoDias(o.tipoDiasReconhecimento),
    marcoRepasse,
    marcoRepasseDescricao: marcoRepasse === 'outro' ? texto(o.marcoRepasseDescricao, 200) : '',
    diasRepasse: dias(o.diasRepasse),
    tipoDiasRepasse: tipoDias(o.tipoDiasRepasse),
    confirmacao: umDe(o.confirmacao, ids(ORIGENS_CONFIRMACAO)),
    observacoes: texto(o.observacoes, 600),
  };
}
export const regraRecebimentoVaziaDe = (r: RegraRecebimento) =>
  !r.liberacao && r.diasReconhecimento === null && !r.marcoRepasse && r.diasRepasse === null && !r.confirmacao && !r.observacoes;

/**
 * Liberação SUGERIDA a partir da frequência da regra de comissão. Frequência (quando a comissão é calculada) e
 * liberação (quando a Inttrix pode cobrar) são conceitos distintos: a sugestão é só ponto de partida, conferível.
 */
export function liberacaoPelaComissao(regra: string | null | undefined, modalidade: Modalidade): TipoLiberacao | null {
  if (regra === 'por_parcela') return modalidade === 'venda' ? 'proporcional' : modalidade === 'locacao_mensal' ? 'mensalidade' : null;
  if (regra === 'mensal_projeto') return modalidade === 'locacao_mensal' ? 'mensalidade' : null;
  if (regra === 'unica_projeto') return modalidade === 'locacao_projeto' ? 'marco_prazo' : null;
  return null;
}

export const diasTexto = (n: number | null, t: TipoDias | null) =>
  n === null ? A_CONFIRMAR : `${n} ${n === 1 ? 'dia' : 'dias'}${t ? (t === 'uteis' ? ' úteis' : ' corridos') : ` (${A_CONFIRMAR.toLowerCase()} se corridos ou úteis)`}`;

/** Linhas de leitura da regra (cadastro do parceiro e cópia congelada no Pedido). */
export function linhasRegraRecebimento(r: RegraRecebimento): { rotulo: string; valor: string }[] {
  const lib = !r.liberacao
    ? A_CONFIRMAR
    : r.liberacao === 'marco_prazo'
      ? `${rotuloDe(MARCOS_LIBERACAO, r.marcoLiberacao)} + ${diasTexto(r.diasLiberacao, r.tipoDiasLiberacao)}`
      : r.liberacao === 'dias_apos_pagamento'
        ? `Pagamento do comprador ao Cliente + ${diasTexto(r.diasLiberacao, r.tipoDiasLiberacao)}`
        : rotuloDe(TIPOS_LIBERACAO, r.liberacao);
  const marco = r.marcoRepasse === 'outro' ? `Outro: ${r.marcoRepasseDescricao || A_CONFIRMAR}` : rotuloDe(MARCOS_REPASSE, r.marcoRepasse);
  const linhas = [{ rotulo: 'Liberação', valor: lib }];
  if (!r.liberacao || LIBERACOES_POR_PAGAMENTO.includes(r.liberacao)) linhas.push({ rotulo: 'Reconhecimento do pagamento', valor: diasTexto(r.diasReconhecimento, r.tipoDiasReconhecimento) });
  linhas.push({ rotulo: 'Repasse', valor: !r.marcoRepasse && r.diasRepasse === null ? A_CONFIRMAR : `${marco} + ${diasTexto(r.diasRepasse, r.tipoDiasRepasse)}` });
  linhas.push({ rotulo: 'Confirmação do valor', valor: rotuloDe(ORIGENS_CONFIRMACAO, r.confirmacao) });
  if (r.observacoes) linhas.push({ rotulo: 'Observações', valor: r.observacoes });
  return linhas;
}

/** Cópia da regra de um parceiro guardada na versão da Proposta no envio (e levada ao Pedido). */
export interface RegraRecebimentoCongelada {
  parceiroId: string;
  parceiro: string;
  regra: RegraRecebimento;
  /** quando o cadastro do parceiro foi atualizado pela última vez antes do envio */
  cadastroAtualizadoEm: string | null;
  congeladaEm: string;
}
